<template>
  <SoPageWithHeader title="Communications">
    <SoButton
      type="text"
      title="Back to summary"
      prepend-icon="arrow-left"
      class="mb-2 px-0"
      data-testid="warrants-comms-back-btn"
      :to="{
        name: RouteNames.AdminServiceWarrantsCommunications,
        params: {
          serviceName: $route.params.serviceName,
          accountNumber: $route.params.accountNumber,
        },
      }"
    />

    <p
      class="mb-1 text-body-1 text-center"
      data-testid="warrants-comms-account"
    >
      Account <strong>{{ accountNumber }}</strong>
    </p>
    <p class="mb-4 text-body-1 text-center" data-testid="warrants-comms-group">
      Communication Group <strong>{{ groupId }}</strong>
    </p>

    <WarrantsDataTableTab
      :headers="headers"
      error-title="Failed to load communications"
      data-testid="warrants-comms"
      :fetcher="fetchCommunications"
      :items-from="itemsFrom"
    >
      <template #[`item.deliveryStatus`]="{ internalItem }">
        <SoChip :text="internalItem.raw.deliveryStatusDisplay" size="small" />
      </template>
    </WarrantsDataTableTab>
  </SoPageWithHeader>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoPageWithHeader from "@/components/templates/SoPageWithHeader.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoChip from "@/components/SoChip.vue"
  import WarrantsDataTableTab from "@/views/admin/warrants/components/WarrantsDataTableTab.vue"
  import { SoDataTableOptions } from "@/components/dataTable/SoDataTableServer.vue"
  import { SoDataTableHeader } from "@/types/components/SoDataTable"
  import {
    createdAtColumn,
    deletedColumn,
    idColumn,
  } from "@/views/admin/warrants/warrantsColumns"
  import {
    WarrantsCommunicationListResult,
    WarrantsCommunicationService,
  } from "@/services/admin/WarrantsCommunicationService"
  import { RouteNames } from "@/types/router"

  const headers: SoDataTableHeader[] = [
    idColumn,
    {
      title: "Comms ID",
      key: "commsId",
      sortable: true,
      sortKey: "comms_id",
      fallbackValue: "—",
    },
    { title: "Type", key: "typeDisplay", sortable: true, sortKey: "type" },
    {
      title: "Delivery Status",
      key: "deliveryStatus",
      sortable: true,
      sortKey: "delivery_status",
    },
    {
      title: "External Status",
      key: "deliveryExternalStatus",
      sortable: false,
      fallbackValue: "—",
    },
    {
      title: "Priority",
      key: "parentPriorityLevel",
      sortable: true,
      sortKey: "parent_priority_level",
    },
    {
      title: "Error",
      key: "errorDescription",
      sortable: false,
      fallbackValue: "—",
    },
    createdAtColumn,
    deletedColumn,
  ]

  export default defineComponent({
    components: {
      SoPageWithHeader,
      SoButton,
      SoChip,
      WarrantsDataTableTab,
    },
    data() {
      return {
        RouteNames,
        headers,
      }
    },
    computed: {
      accountNumber(): string {
        return this.$route.params.accountNumber as string
      },
      groupId(): number {
        return Number(this.$route.params.groupId)
      },
    },
    methods: {
      fetchCommunications({ page, pageSize, orderBy }: SoDataTableOptions) {
        return WarrantsCommunicationService.getCommunications(
          this.groupId,
          page,
          pageSize,
          orderBy,
        )
      },
      itemsFrom(result: WarrantsCommunicationListResult) {
        return result.communications
      },
    },
  })
</script>
